import * as Dialog from '@radix-ui/react-dialog'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'

import { IssueModalDetails } from './IssueModalDetails'
import { IssueModalGallery } from './IssueModalGallery'
import { useGisIssueDetail } from '../../../hooks/useGisIssueDetail'

import type { GisIssue, GisIssueDetail } from '../../../api/types'

/** Previous / next issue ids in the current filtered+sorted list (nulls at the ends). */
function neighbors(rows: GisIssue[], id: string | null): { prevId: string | null; nextId: string | null } {
  if (id === null) {
    return { prevId: null, nextId: null }
  }
  const i = rows.findIndex((r) => r.id === id)
  if (i < 0) {
    return { prevId: null, nextId: null }
  }
  return {
    prevId: rows[i - 1]?.id ?? null,
    nextId: rows[i + 1]?.id ?? null,
  }
}

/**
 * Circular issue prev/next button pinned to the modal's vertical middle (classic side-nav). Renders
 * nothing at a list end — no dead affordance. `active:scale-95` gives the press its feedback.
 */
function SideNavButton({ dir, target, onNavigate }: {
  dir: -1 | 1
  target: string | null
  onNavigate: (id: string) => void
}) {
  if (target === null) {
    return null
  }
  const Icon = dir === -1 ? ChevronLeft : ChevronRight
  return (
    <button
      type="button"
      onClick={() => onNavigate(target)}
      aria-label={dir === -1 ? 'Previous issue' : 'Next issue'}
      className={`absolute top-1/2 z-20 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-full border border-gis-subheading bg-gis-subheading text-white shadow-md backdrop-blur transition-transform duration-150 ease-out hover:bg-gis-heading active:scale-95 focus-visible:ring-2 focus-visible:ring-blue-500/60 ${dir === -1 ? 'left-3' : 'right-3'}`}
    >
      <Icon className="h-5 w-5" />
    </button>
  )
}

/** Breadcrumb + title on the left, close on the right. Issue navigation lives in the side arrows. */
function ModalHeader({ title, breadcrumb, position }: {
  title: string
  breadcrumb: string | null
  /** This issue's position in the current filtered list (1-based), or null when unknown. */
  position: { current: number; total: number } | null
}) {
  return (
    <div className="flex items-center gap-3 border-b border-gis-line px-5 py-3">
      <div className="min-w-0 flex-1">
        {breadcrumb && (
          <div className="truncate text-[11px] font-medium text-gis-soft">{breadcrumb}</div>
        )}
        <Dialog.Title className="truncate text-[15px] leading-[1.25] font-bold tracking-[-.01em] text-gis-blue">
          {title}
        </Dialog.Title>
      </div>
      {position && (
        <span className="flex-none rounded-full bg-gis-panel px-2 py-0.5 text-[11px] font-semibold tabular-nums text-gis-soft">
          {position.current} / {position.total}
        </span>
      )}
      <Dialog.Close
        aria-label="Close"
        className="grid h-8 w-8 flex-none place-items-center rounded-full border border-gis-line text-gis-soft transition hover:bg-gis-track hover:text-gis-ink focus-visible:ring-2 focus-visible:ring-blue-500/60"
      >
        <X className="h-[17px] w-[17px]" />
      </Dialog.Close>
    </div>
  )
}

/** Two-column skeleton that mirrors the real layout (image left, stacked lines right) — no CLS. */
function ModalSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-6 @[680px]/modal:grid-cols-2" aria-hidden>
      <div className="aspect-[4/3] w-full animate-pulse rounded-2xl bg-gis-track" />
      <div className="space-y-3">
        <div className="h-6 w-24 animate-pulse rounded-full bg-gis-track" />
        <div className="h-6 w-3/4 animate-pulse rounded bg-gis-track" />
        <div className="h-4 w-1/2 animate-pulse rounded bg-gis-track" />
        <div className="mt-4 h-20 w-full animate-pulse rounded-2xl bg-gis-track" />
        <div className="h-24 w-full animate-pulse rounded-2xl bg-gis-track" />
      </div>
    </div>
  )
}

function ModalError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="grid min-h-[280px] place-items-center text-center text-[12.5px] text-gis-soft">
      <div>
        Couldn't load this issue.{' '}
        <button type="button" onClick={onRetry} className="font-semibold text-gis-navy underline">
          Retry
        </button>
      </div>
    </div>
  )
}

/** Header title + site breadcrumb, using the loaded detail and falling back to the list row so the
 *  header never flashes empty while the detail query is in flight. */
function deriveHeader(data: GisIssueDetail | undefined, row: GisIssue | undefined) {
  return {
    title: data?.title ?? row?.title ?? 'Issue',
    breadcrumb: data?.siteName ?? row?.siteName ?? null,
  }
}

/**
 * Body state machine: skeleton while loading, error+retry on failure, else the two-column layout.
 * On a wide modal the grid fills the body height and the image column stays FIXED while only the
 * details column scrolls; below the container breakpoint it stacks and the whole body scrolls.
 */
function ModalBody({ data, isPending, isError, onRetry }: {
  data: GisIssueDetail | undefined
  isPending: boolean
  isError: boolean
  onRetry: () => void
}) {
  if (isPending) {
    return <div className="p-4"><ModalSkeleton /></div>
  }
  if (isError || !data) {
    return <ModalError onRetry={onRetry} />
  }
  // Inner scroller: narrow (stacked) → the whole body scrolls; wide → the grid fills the modal's
  // FIXED height (one `minmax(0,1fr)` row) and each column owns its own overflow. The
  // `@container/modal` is declared one level up (on the shell wrapper) so these `@[680px]` queries
  // actually resolve (an element can't match its own container query). The modal frame is fixed-size
  // (see Dialog.Content), so neither column ever drives the popup's dimensions.
  return (
    <div className="h-full min-h-0 overflow-y-auto @[680px]/modal:overflow-hidden">
      <div className="grid grid-cols-1 @[680px]/modal:h-full @[680px]/modal:min-h-0 @[680px]/modal:grid-cols-2 @[680px]/modal:grid-rows-[minmax(0,1fr)]">
        {/* Image — its own fixed, tinted, divided section (never scrolls on a wide modal). */}
        <div className="flex flex-col justify-start border-b border-gis-line bg-gis-panel p-4 @[680px]/modal:min-h-0 @[680px]/modal:overflow-hidden @[680px]/modal:border-r @[680px]/modal:border-b-0">
          <IssueModalGallery detail={data} />
        </div>
        {/* Details — the only column that scrolls on a wide modal. */}
        <div className="p-4 @[680px]/modal:h-full @[680px]/modal:min-h-0 @[680px]/modal:overflow-y-auto">
          <IssueModalDetails detail={data} />
        </div>
      </div>
    </div>
  )
}

interface IssueGalleryModalProps {
  /** True in Gallery view; the modal self-guards so the parent adds no branching. */
  open: boolean
  /** The clicked issue, or null when nothing is open. */
  issueId: string | null
  /** The current filtered+sorted list, for header ‹ › issue navigation. */
  rows: GisIssue[]
  /** Jump to another issue (header ‹ ›) — sets the parent's gallery id. */
  onNavigate: (id: string) => void
  onClose: () => void
}

/**
 * Centered issue-detail modal for the Gallery view: image carousel (left) + full detail read-out
 * (right). Self-guarding — renders nothing unless `open` AND an `issueId` are set — so `IssuesDashboard`
 * stays branch-free and the drawer path is untouched. Esc / click-outside / X close it for free
 * (Radix Dialog); ‹ › walk the filtered list without closing.
 */
export function IssueGalleryModal({ open, issueId, rows, onNavigate, onClose }: IssueGalleryModalProps) {
  const { data, isPending, isError, refetch } = useGisIssueDetail(issueId ?? '')
  const show = open && issueId !== null
  const { prevId, nextId } = neighbors(rows, issueId)
  const index = rows.findIndex((r) => r.id === issueId)
  const position = index >= 0 ? { current: index + 1, total: rows.length } : null
  const { title, breadcrumb } = deriveHeader(data, rows.find((r) => r.id === issueId))

  return (
    <Dialog.Root open={show} onOpenChange={(o) => { if (!o) { onClose() } }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[9998] bg-[#0b1220]/70 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in" />
        <Dialog.Content
          // FIXED height + width (not content-sized): prev/next keeps the frame rock-steady while
          // the next issue loads — the skeleton/detail swap can never resize the popup.
          className="fixed top-1/2 left-1/2 z-[9999] flex h-[min(660px,88vh)] w-[min(92vw,1012px)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl bg-white shadow-2xl focus:outline-none data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:zoom-in-95"
          aria-describedby={undefined}
        >
          <ModalHeader title={title} breadcrumb={breadcrumb} position={position} />
          <div className="@container/modal min-h-0 flex-1 overflow-hidden">
            <ModalBody
              data={data}
              isPending={isPending}
              isError={isError}
              onRetry={() => void refetch()}
            />
          </div>
          <SideNavButton dir={-1} target={prevId} onNavigate={onNavigate} />
          <SideNavButton dir={1} target={nextId} onNavigate={onNavigate} />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
